import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Paper,
  Collapse,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  InsertDriveFile as FileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import axios from "axios";
import { hostname } from "../../utils";
import { RemoteAgent, FilesystemNode } from "../../constants";

interface WorkspaceFilesDialogProps {
  open: boolean;
  onClose: () => void;
  agent: RemoteAgent | null;
  getToken: () => Promise<string>;
}

// Format file size for display
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

// Format date for display
const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleString();
};

// Check if file is likely text-based
const isTextFile = (name: string): boolean => {
  const textExtensions = [
    ".txt", ".md", ".json", ".js", ".ts", ".tsx", ".jsx", ".py", ".html", ".css",
    ".scss", ".yaml", ".yml", ".xml", ".sh", ".bash", ".env", ".gitignore",
    ".toml", ".ini", ".cfg", ".conf", ".log", ".csv", ".sql", ".r", ".rb",
    ".php", ".go", ".rs", ".c", ".cpp", ".h", ".hpp", ".java", ".kt", ".swift",
  ];
  const lowerName = name.toLowerCase();
  return textExtensions.some(ext => lowerName.endsWith(ext)) || !lowerName.includes(".");
};

interface FileNodeProps {
  node: FilesystemNode;
  depth: number;
  basePath: string;
  onOpenFile: (path: string, name: string) => void;
  expandedPaths: Set<string>;
  onToggleExpand: (path: string) => void;
}

const FileNode: React.FC<FileNodeProps> = ({
  node,
  depth,
  basePath,
  onOpenFile,
  expandedPaths,
  onToggleExpand,
}) => {
  const fullPath = basePath ? `${basePath}/${node.name}` : node.name;
  const isExpanded = expandedPaths.has(fullPath);
  const isDirectory = node.type === "directory";
  const hasChildren = isDirectory && node.children && node.children.length > 0;

  const handleClick = () => {
    if (isDirectory) {
      onToggleExpand(fullPath);
    } else if (isTextFile(node.name)) {
      onOpenFile(fullPath, node.name);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          px: 1,
          pl: 1 + depth * 2,
          cursor: isDirectory || isTextFile(node.name) ? "pointer" : "default",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          borderRadius: 1,
        }}
        onClick={handleClick}
      >
        {isDirectory ? (
          <>
            <IconButton size="small" sx={{ p: 0.25, mr: 0.5 }}>
              {hasChildren ? (
                isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
            </IconButton>
            {isExpanded ? (
              <FolderOpenIcon sx={{ color: "primary.main", mr: 1, fontSize: 20 }} />
            ) : (
              <FolderIcon sx={{ color: "primary.main", mr: 1, fontSize: 20 }} />
            )}
          </>
        ) : (
          <>
            <Box sx={{ width: 28 }} />
            <FileIcon sx={{ color: "text.secondary", mr: 1, fontSize: 20 }} />
          </>
        )}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            fontFamily: "monospace",
            fontSize: "0.85rem",
          }}
        >
          {node.name}
        </Typography>
        {!isDirectory && node.size !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
            {formatFileSize(node.size)}
          </Typography>
        )}
        {node.truncated && (
          <Tooltip title="Directory listing was truncated due to depth limit">
            <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
              ...
            </Typography>
          </Tooltip>
        )}
      </Box>

      {isDirectory && hasChildren && (
        <Collapse in={isExpanded}>
          {node.children!.map((child, idx) => (
            <FileNode
              key={`${fullPath}/${child.name}-${idx}`}
              node={child}
              depth={depth + 1}
              basePath={fullPath}
              onOpenFile={onOpenFile}
              expandedPaths={expandedPaths}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
};

const WorkspaceFilesDialog: React.FC<WorkspaceFilesDialogProps> = ({
  open,
  onClose,
  agent,
  getToken,
}) => {
  const [filesystemTree, setFilesystemTree] = useState<FilesystemNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // File editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingFile, setEditingFile] = useState<{ path: string; name: string } | null>(null);
  const [fileContent, setFileContent] = useState<string>("");
  const [originalContent, setOriginalContent] = useState<string>("");
  const [loadingFile, setLoadingFile] = useState(false);
  const [savingFile, setSavingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  // Fetch filesystem tree for selected agent
  const fetchFilesystemTree = useCallback(async () => {
    if (!agent) return;

    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/remote-agents/${agent.id}/filesystem`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFilesystemTree(response.data.filesystemTree || null);
      setLastUpdated(response.data.filesystemTreeUpdatedAt || null);
    } catch (err) {
      console.error("Error fetching filesystem:", err);
      setError("Failed to load workspace files");
    } finally {
      setLoading(false);
    }
  }, [agent, getToken]);

  // Load when dialog opens
  useEffect(() => {
    if (open && agent) {
      fetchFilesystemTree();
      setExpandedPaths(new Set());
    }
  }, [open, agent, fetchFilesystemTree]);

  // Refresh filesystem tree
  const handleRefresh = async () => {
    if (!agent) return;

    try {
      setRefreshing(true);
      setError(null);
      const token = await getToken();

      // Request a refresh
      const response = await axios.post(
        `${hostname}/remote-agents/${agent.id}/filesystem/refresh`,
        { depth: 3 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const commandId = response.data.commandId;

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30; // 15 seconds
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const cmdResponse = await axios.get(
          `${hostname}/remote-agents/commands/${commandId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cmdResponse.data.status === "completed") {
          // Fetch the updated tree
          await fetchFilesystemTree();
          break;
        } else if (cmdResponse.data.status === "failed") {
          throw new Error(cmdResponse.data.errorMessage || "Refresh failed");
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        throw new Error("Refresh timed out");
      }
    } catch (err) {
      console.error("Error refreshing filesystem:", err);
      setError(err instanceof Error ? err.message : "Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // Open file for viewing/editing
  const handleOpenFile = async (path: string, name: string) => {
    if (!agent) return;

    setEditingFile({ path, name });
    setEditorOpen(true);
    setLoadingFile(true);
    setFileError(null);
    setFileContent("");
    setOriginalContent("");

    try {
      const token = await getToken();

      // Request file read
      const response = await axios.post(
        `${hostname}/remote-agents/${agent.id}/filesystem/read`,
        { path },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const commandId = response.data.commandId;

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const cmdResponse = await axios.get(
          `${hostname}/remote-agents/commands/${commandId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cmdResponse.data.status === "completed") {
          const content = cmdResponse.data.stdout || "";
          setFileContent(content);
          setOriginalContent(content);
          break;
        } else if (cmdResponse.data.status === "failed") {
          throw new Error(cmdResponse.data.errorMessage || "Failed to read file");
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        throw new Error("Read timed out");
      }
    } catch (err) {
      console.error("Error reading file:", err);
      setFileError(err instanceof Error ? err.message : "Failed to read file");
    } finally {
      setLoadingFile(false);
    }
  };

  // Save file
  const handleSaveFile = async () => {
    if (!agent || !editingFile) return;

    setSavingFile(true);
    setFileError(null);

    try {
      const token = await getToken();

      // Request file write
      const response = await axios.post(
        `${hostname}/remote-agents/${agent.id}/filesystem/write`,
        { path: editingFile.path, content: fileContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const commandId = response.data.commandId;

      // Poll for completion
      let attempts = 0;
      const maxAttempts = 30;
      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 500));
        const cmdResponse = await axios.get(
          `${hostname}/remote-agents/commands/${commandId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (cmdResponse.data.status === "completed") {
          setOriginalContent(fileContent);
          // Refresh the filesystem tree to show updated modified time
          await fetchFilesystemTree();
          break;
        } else if (cmdResponse.data.status === "failed") {
          throw new Error(cmdResponse.data.errorMessage || "Failed to save file");
        }
        attempts++;
      }

      if (attempts >= maxAttempts) {
        throw new Error("Save timed out");
      }
    } catch (err) {
      console.error("Error saving file:", err);
      setFileError(err instanceof Error ? err.message : "Failed to save file");
    } finally {
      setSavingFile(false);
    }
  };

  // Close editor
  const handleCloseEditor = () => {
    setEditorOpen(false);
    setEditingFile(null);
    setFileContent("");
    setOriginalContent("");
    setFileError(null);
  };

  // Toggle expand
  const handleToggleExpand = (path: string) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const hasChanges = fileContent !== originalContent;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FolderIcon />
            <Box>
              <Typography variant="h6">
                Workspace Files
              </Typography>
              {agent && (
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                  {agent.name} - {agent.workspacePath || "~/workspace"}
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Updated: {formatDate(lastUpdated)}
              </Typography>
            )}
            <IconButton
              size="small"
              onClick={handleRefresh}
              disabled={refreshing}
              title="Refresh files"
            >
              {refreshing ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              overflow: "auto",
              p: 1,
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : filesystemTree ? (
              filesystemTree.children && filesystemTree.children.length > 0 ? (
                filesystemTree.children.map((node, idx) => (
                  <FileNode
                    key={`${node.name}-${idx}`}
                    node={node}
                    depth={0}
                    basePath=""
                    onOpenFile={handleOpenFile}
                    expandedPaths={expandedPaths}
                    onToggleExpand={handleToggleExpand}
                  />
                ))
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
                  Workspace is empty
                </Typography>
              )
            ) : (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  No filesystem data available
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                >
                  Load Files
                </Button>
              </Box>
            )}
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* File Editor Dialog */}
      <Dialog
        open={editorOpen}
        onClose={handleCloseEditor}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "80vh" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FileIcon />
          <Typography variant="h6" sx={{ flex: 1, fontFamily: "monospace" }}>
            {editingFile?.name}
          </Typography>
          <IconButton size="small" onClick={handleCloseEditor}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
          {fileError && (
            <Alert severity="error" sx={{ mx: 2, mt: 1 }}>
              {fileError}
            </Alert>
          )}
          {loadingFile ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              multiline
              fullWidth
              value={fileContent}
              onChange={(e) => setFileContent(e.target.value)}
              sx={{
                flex: 1,
                "& .MuiInputBase-root": {
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  height: "100%",
                  alignItems: "flex-start",
                },
                "& .MuiInputBase-input": {
                  height: "100% !important",
                  overflow: "auto !important",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              placeholder="File content..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
            {editingFile?.path}
          </Typography>
          <Button onClick={handleCloseEditor}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveFile}
            disabled={!hasChanges || savingFile || loadingFile}
            startIcon={savingFile ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WorkspaceFilesDialog;
